// Publikus súgó — a nyitólap.
//
// Ez az EGYETLEN felület, amit ügyfelek látnak, ezért ugyanaz a forma, mint a belső kezdőlapé:
// egy kérdés, egy kereső, alatta a termékek kártyái. A különbség a halmaz (csak nyilvános cikkek)
// és az, hogy itt nincs személyes megszólítás — nem tudjuk, ki jött.
//
// A kereső találatai a lapon belül jelennek meg: egy súgóban a keresés nem külön oldal, hanem a
// nyitólap másik állapota.
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { SearchScope } from '@morgens-hub/contracts';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { splitSpaceName } from '@/features/knowledge/lib/grouping';
import { iconForSpace, topicAt, topicIndex, topicVars } from '@/features/knowledge/lib/topic';
import { usePublicSearch, usePublicTree } from '@/features/public/api';
import { PublicShell } from '@/features/public/components/public-shell';
import { SearchScopeToggle } from '@/features/search/components/search-scope-toggle';
import { SearchSnippet } from '@/features/search/components/search-snippet';
import { useDebouncedValue } from '@/lib/use-debounced-value';

export const Route = createFileRoute('/sugo/')({
  // A kifejezés URL-állapot: a fejléc-kereső ide navigál, és így a találat MEGOSZTHATÓ (ugyanaz a
  // minta, mint a belső `/kereses`-nél). A mezőbe gépelés közben nem írjuk az URL-t — az minden
  // karakternél history-bejegyzést vagy replace-zajt jelentene; Enterre viszont rögzül.
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === 'string' && search.q ? { q: search.q } : {},
  component: PublicHome,
});

function PublicHome(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { q = '' } = Route.useSearch();
  const [query, setQuery] = useState(q);
  // A hatókör LOKÁLIS state — a súgó-kereső kifejezése is state-alapú (nem URL-param, ellentétben a
  // belső `/kereses`-sel), a scope ugyanazt a mintát követi: nincs megosztható/visszalapozható link.
  const [scope, setScope] = useState<SearchScope>('all');
  // Címsorból (megosztott link, vissza-gomb, fejléc-kereső) érkező kifejezés kerüljön a mezőbe.
  useEffect(() => {
    setQuery(q);
  }, [q]);
  const tree = usePublicTree();
  const debouncedQuery = useDebouncedValue(query.trim(), 250);
  const search = usePublicSearch(debouncedQuery, scope);

  const body = tree.data?.status === 200 ? tree.data.body : null;
  // Üres terület nem való a súgó nyitólapjára: egy 0 cikkes kártya csak csalódás.
  const spaces = useMemo(
    () => (body?.spaces ?? []).filter((space) => space.articleCount > 0),
    [body],
  );
  const results = search.data?.status === 200 ? search.data.body : null;
  // Opcionális mező: régi/cache-elt válasz nem tartalmazza — ilyenkor egyszerűen nincs struktúra-sáv.
  const structureHits = results?.structureHits ?? [];
  const searching = query.trim().length > 0;

  // A publikus felület nem éri el a belső fát, ezért a színkiosztás a SAJÁT listájából készül —
  // ugyanaz a szabály (termékek ábécé-sorrendje), csak szűkebb halmazon.
  const accents = useMemo(() => topicIndex((body?.spaces ?? []).map((s) => s.name)), [body]);
  const varsFor = (name: string): React.CSSProperties => {
    const position = accents.get(splitSpaceName(name).prefix ?? name);
    return topicVars(topicAt(position ?? 0));
  };

  return (
    <PublicShell wide showSearch={false}>
      <section className="border-border relative overflow-hidden border-b">
        <div className="hero-grid pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative mx-auto max-w-2xl px-5 pb-12 pt-14 text-center">
          <h1 className="text-read-3xl font-semibold tracking-tight">{t('public.hero')}</h1>
          <p className="text-muted-foreground text-read-sm mt-3">{t('public.subtitle')}</p>

          <form
            role="search"
            className="relative mt-7"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = query.trim();
              // Enterre rögzítjük az URL-be — innentől megosztható és visszalapozható.
              void navigate({ to: '/sugo', search: trimmed ? { q: trimmed } : {} });
            }}
          >
            <MagnifyingGlassIcon
              className="text-subtle pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={t('public.searchPlaceholder')}
              aria-label={t('public.searchPlaceholder')}
              className="h-14 rounded-2xl pl-14 text-base shadow-sm"
            />
          </form>

          {/* A hatókör a mezővel együtt mindig látszik — üres kifejezésnél is beállítható, mielőtt
              az első karakter elindítaná a keresést (lásd a belső `/kereses` mintáját). */}
          <div className="mt-4 flex justify-center">
            <SearchScopeToggle value={scope} onChange={setScope} />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-5 py-10">
        {searching ? (
          <section aria-live="polite">
            {search.isLoading && <Skeleton className="h-32 w-full" />}

            {results?.degraded && (
              <p className="text-destructive mt-4 text-sm">{t('search.degraded')}</p>
            )}

            {structureHits.length > 0 && (
              <ul className="divide-border mt-4 divide-y">
                {structureHits.map((hit) => (
                  <li key={hit.id} className="flex items-center gap-2 py-3">
                    <span className="border-border text-muted-foreground shrink-0 rounded-full border px-2 py-0.5 text-xs uppercase tracking-[0.08em]">
                      {t(`search.kind.${hit.kind}`)}
                    </span>
                    <Link
                      to="/sugo/$spaceSlug"
                      params={{ spaceSlug: hit.spaceSlug }}
                      // A `hash` prop `exactOptionalPropertyTypes` alatt nem fogad explicit `undefined`-t —
                      // a `category`-találat `categorySlug`-ja mindig kitöltött, a `null` csak a `space`
                      // ágnál fordul elő (ott a publikus szekció-oldalon nincs is horgony).
                      {...(hit.categorySlug === null ? {} : { hash: hit.categorySlug })}
                      className="hover:text-topic font-medium"
                    >
                      {hit.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {results &&
              !results.degraded &&
              results.hits.length === 0 &&
              structureHits.length === 0 && (
                <p className="text-muted-foreground mt-4 text-sm">
                  {t('search.empty', { query: query.trim() })}
                </p>
              )}

            {results && results.hits.length > 0 && (
              <>
                <p className="text-subtle text-sm">
                  {t('search.resultCount', { count: results.hits.length })}
                </p>
                <ul className="divide-border mt-3 divide-y">
                  {results.hits.map((hit) => (
                    <li key={hit.chunkId} data-topic style={varsFor(hit.spaceName)}>
                      <Link
                        to="/sugo/$spaceSlug/$categorySlug/$articleSlug"
                        params={{
                          spaceSlug: hit.spaceSlug,
                          categorySlug: hit.categorySlug,
                          articleSlug: hit.articleSlug,
                        }}
                        className="index-row hover:bg-muted/50 group block py-4"
                      >
                        <span className="text-topic block text-xs font-semibold uppercase tracking-[0.1em]">
                          {hit.spaceName}
                        </span>
                        <span className="group-hover:text-topic text-read-lg mt-1 block font-medium transition-colors duration-150">
                          {hit.articleTitle}
                        </span>
                        <SearchSnippet snippet={hit.snippet} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        ) : (
          <section>
            {tree.isLoading && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-40 w-full rounded-xl" />
                ))}
              </div>
            )}

            {!tree.isLoading && !body && (
              <p className="text-destructive text-sm">{t('public.loadError')}</p>
            )}

            {body && spaces.length === 0 && (
              <p className="text-muted-foreground text-sm">{t('public.empty')}</p>
            )}

            {spaces.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {spaces.map((space) => {
                  const { prefix, label } = splitSpaceName(space.name);
                  const Icon = iconForSpace(space.name, space.icon);
                  return (
                    <Link
                      key={space.id}
                      to="/sugo/$spaceSlug"
                      params={{ spaceSlug: space.slug }}
                      data-topic
                      style={varsFor(space.name)}
                      className="border-border hover:border-topic/50 group flex flex-col overflow-hidden rounded-xl border transition-colors duration-150"
                    >
                      <div className="bg-topic h-1" aria-hidden />
                      <div className="bg-topic/[0.07] group-hover:bg-topic/[0.12] flex h-20 items-center justify-center transition-colors duration-150">
                        <Icon className="text-topic h-7 w-7" aria-hidden />
                      </div>
                      <div className="flex flex-1 flex-col p-4">
                        {prefix && (
                          <span className="text-topic text-2xs truncate font-semibold uppercase tracking-[0.12em]">
                            {prefix}
                          </span>
                        )}
                        <span className="group-hover:text-topic mt-1 font-medium leading-snug transition-colors duration-150">
                          {label}
                        </span>
                        <span className="text-subtle mt-auto pt-3 font-mono text-sm tabular-nums">
                          {t('knowledge.articleCount', { count: space.articleCount })}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </PublicShell>
  );
}
